import {Component} from '@theme/component';

/** Horizontal distance (px) a touch/pen swipe must travel to change slide. */
const SWIPE_THRESHOLD = 40;

/** Time (ms) the outgoing plate stays under the incoming one; just over the 900ms CSS crossfade. */
const CROSSFADE_MS = 1000;

/**
 * Crossfade "still plate" hero. The hand/backdrop never moves: only the slide plates fade (opacity) and
 * the captions wipe (transform), driven by the `is-active` / `is-leaving` classes on each slide.
 *
 * Autoplay advances when the active segment's CSS fill animation ends, so pausing (user, hover, focus,
 * out of viewport, hidden tab) is just `animation-play-state` via `data-playing`.
 *
 * @typedef {object} LunHeroWristRefs
 * @property {HTMLElement[]} slides - One element per slide block.
 * @property {HTMLButtonElement[] | undefined} segments - Pagination / progress buttons.
 * @property {HTMLElement | undefined} counter - Current slide number (decorative).
 * @property {HTMLElement} liveRegion - Polite live region, written only on user-driven changes.
 *
 * @extends {Component<LunHeroWristRefs>}
 */
export class LunHeroWristComponent extends Component {
    requiredRefs = ['liveRegion'];

    /** @type {AbortController | null} */
    #abortController = null;
    /** @type {IntersectionObserver | null} */
    #intersectionObserver = null;
    /** @type {number | undefined} */
    #leaveTimer;
    /** @type {number | undefined} */
    #announceTimer;

    #current = 0;
    #hovered = false;
    #focused = false;
    #inView = false;
    #editorHold = false;
    #reducedMotion = matchMedia('(prefers-reduced-motion: reduce)');

    /** @type {{ x: number, y: number, id: number } | null} */
    #swipe = null;
    #swiped = false;

    get #slides() {
        return this.refs.slides ?? [];
    }

    get #count() {
        return this.#slides.length;
    }

    connectedCallback() {
        super.connectedCallback();

        this.#abortController = new AbortController();
        const {signal} = this.#abortController;

        const active = this.#slides.findIndex((slide) => slide.classList.contains('is-active'));
        this.#current = Math.max(active, 0);
        this.#render();

        this.dataset.ready = '';

        this.addEventListener('animationend', this.#handleAnimationEnd, {signal});
        this.addEventListener('pointerenter', this.#handlePointerEnter, {signal});
        this.addEventListener('pointerleave', this.#handlePointerLeave, {signal});
        this.addEventListener('focusin', this.#handleFocusIn, {signal});
        this.addEventListener('focusout', this.#handleFocusOut, {signal});
        this.addEventListener('keydown', this.#handleKeydown, {signal});
        this.addEventListener('pointerdown', this.#handlePointerDown, {signal});
        this.addEventListener('pointerup', this.#handlePointerUp, {signal});
        this.addEventListener('pointercancel', this.#handlePointerCancel, {signal});
        // Swallow the click that ends a swipe which started on a link or button.
        this.addEventListener('click', this.#handleClickCapture, {signal, capture: true});

        document.addEventListener('visibilitychange', this.#updatePlaying, {signal});
        this.#reducedMotion.addEventListener('change', this.#updatePlaying, {signal});

        if (window.Shopify?.designMode) {
            document.addEventListener('shopify:block:select', this.#handleBlockSelect, {signal});
            document.addEventListener('shopify:block:deselect', this.#handleBlockDeselect, {signal});
            document.addEventListener('shopify:section:deselect', this.#handleBlockDeselect, {signal});
        }

        this.#intersectionObserver = new IntersectionObserver(
            (entries) => {
                this.#inView = entries.some((entry) => entry.isIntersecting);
                this.#updatePlaying();
            },
            {threshold: 0.35}
        );
        this.#intersectionObserver.observe(this);

        this.#updatePlaying();
    }

    /**
     * Re-applies the current state after the Section Rendering API morphs the DOM back to server state.
     */
    updatedCallback() {
        super.updatedCallback();

        this.#current = Math.min(this.#current, Math.max(this.#count - 1, 0));
        this.#render();
        this.#updatePlaying();
    }

    disconnectedCallback() {
        super.disconnectedCallback();

        this.#abortController?.abort();
        this.#intersectionObserver?.disconnect();
        clearTimeout(this.#leaveTimer);
        clearTimeout(this.#announceTimer);
    }

    /** Shows the next slide (wraps). Announced, because it is user-driven. */
    next() {
        this.#go(this.#current + 1, true);
    }

    /** Shows the previous slide (wraps). Announced, because it is user-driven. */
    previous() {
        this.#go(this.#current - 1, true);
    }

    /**
     * Shows a specific slide.
     * @param {number} index - Zero-based slide index.
     */
    select(index) {
        this.#go(Number(index), true);
    }

    /**
     * Changes slide.
     * @param {number} target - Zero-based index; wraps around.
     * @param {boolean} announce - Whether to announce through the live region (user-driven changes only).
     */
    #go(target, announce) {
        const count = this.#count;
        if (count < 2) return;

        const index = ((target % count) + count) % count;
        if (index === this.#current) return;

        const slides = this.#slides;
        const outgoing = slides[this.#current];
        const incoming = slides[index];
        if (!outgoing || !incoming) return;

        // A slide that is returning must not keep its leaving state.
        incoming.classList.remove('is-leaving');
        outgoing.classList.remove('is-active');
        outgoing.classList.add('is-leaving');
        incoming.classList.add('is-active');

        this.#current = index;
        this.#syncAttributes();

        // Keep the previous plate opaque underneath until the incoming one has fully faded in.
        clearTimeout(this.#leaveTimer);
        this.#leaveTimer = window.setTimeout(() => {
            for (const slide of this.#slides) slide.classList.remove('is-leaving');
        }, CROSSFADE_MS);

        if (announce) this.#announce(incoming);
    }

    /** Applies the current index to every slide without animating. */
    #render() {
        this.#slides.forEach((slide, i) => {
            slide.classList.toggle('is-active', i === this.#current);
            slide.classList.remove('is-leaving');
        });
        this.#syncAttributes();
    }

    /** Syncs inert, aria-current and the counter with the current index. */
    #syncAttributes() {
        this.#slides.forEach((slide, i) => {
            const caption = slide.querySelector('.lun-hero__caption');
            if (caption instanceof HTMLElement) caption.inert = i !== this.#current;
        });

        this.refs.segments?.forEach((segment, i) => {
            if (i === this.#current) {
                segment.setAttribute('aria-current', 'true');
            } else {
                segment.removeAttribute('aria-current');
            }
        });

        const {counter} = this.refs;
        if (counter) counter.textContent = String(this.#current + 1).padStart(2, '0');
    }

    /**
     * @param {HTMLElement} slide
     */
    #announce(slide) {
        const title = slide.querySelector('.lun-hero__title')?.textContent?.trim() ?? '';
        const label = slide.getAttribute('aria-label') ?? '';
        const {liveRegion} = this.refs;

        // Clear first so identical text is announced again.
        liveRegion.textContent = '';
        clearTimeout(this.#announceTimer);
        this.#announceTimer = window.setTimeout(() => {
            liveRegion.textContent = [label, title].filter(Boolean).join(': ');
        }, 50);
    }

    /** Recomputes whether the autoplay progress (and therefore autoplay) should be running. */
    #updatePlaying = () => {
        const enabled = this.dataset.autoplay === 'true' && this.#count > 1;
        const playing =
            enabled &&
            !this.#hovered &&
            !this.#focused &&
            !this.#editorHold &&
            this.#inView &&
            !document.hidden &&
            !this.#reducedMotion.matches;

        this.dataset.playing = String(playing);
    };

    /** @param {AnimationEvent} event */
    #handleAnimationEnd = (event) => {
        if (event.animationName !== 'lun-hero-fill') return;
        if (this.dataset.playing !== 'true') return;

        this.#go(this.#current + 1, false);
    };

    /** @param {PointerEvent} event */
    #handlePointerEnter = (event) => {
        if (event.pointerType !== 'mouse') return;
        this.#hovered = true;
        this.#updatePlaying();
    };

    #handlePointerLeave = () => {
        this.#hovered = false;
        this.#updatePlaying();
    };

    /** @param {FocusEvent} event */
    #handleFocusIn = (event) => {
        // Only keyboard focus holds autoplay; a mouse click on a control must not freeze it forever.
        this.#focused = event.target instanceof Element && event.target.matches(':focus-visible');
        this.#updatePlaying();
    };

    /** @param {FocusEvent} event */
    #handleFocusOut = (event) => {
        if (event.relatedTarget instanceof Node && this.contains(event.relatedTarget)) return;
        this.#focused = false;
        this.#updatePlaying();
    };

    /** @param {KeyboardEvent} event */
    #handleKeydown = (event) => {
        if (event.altKey || event.ctrlKey || event.metaKey || event.shiftKey) return;
        if (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight') return;

        const target = event.target;
        if (target instanceof HTMLElement && target.matches('input, select, textarea, [contenteditable]')) return;

        const rtl = getComputedStyle(this).direction === 'rtl';
        const forward = (event.key === 'ArrowRight') !== rtl;

        event.preventDefault();
        if (forward) this.next();
        else this.previous();
    };

    /** @param {PointerEvent} event */
    #handlePointerDown = (event) => {
        this.#swiped = false;
        if (event.pointerType === 'mouse' || !event.isPrimary) return;

        this.#swipe = {x: event.clientX, y: event.clientY, id: event.pointerId};
    };

    /** @param {PointerEvent} event */
    #handlePointerUp = (event) => {
        const swipe = this.#swipe;
        this.#swipe = null;
        if (!swipe || swipe.id !== event.pointerId) return;

        const dx = event.clientX - swipe.x;
        const dy = event.clientY - swipe.y;
        if (Math.abs(dx) < SWIPE_THRESHOLD || Math.abs(dx) < Math.abs(dy) * 1.5) return;

        this.#swiped = true;
        const rtl = getComputedStyle(this).direction === 'rtl';
        // Dragging towards the start edge reveals the next slide.
        if (dx < 0 !== rtl) this.next();
        else this.previous();
    };

    #handlePointerCancel = () => {
        this.#swipe = null;
    };

    /** @param {MouseEvent} event */
    #handleClickCapture = (event) => {
        if (!this.#swiped) return;
        this.#swiped = false;
        event.preventDefault();
        event.stopPropagation();
    };

    /** @param {Event} event */
    #handleBlockSelect = (event) => {
        const target = event.target;
        if (!(target instanceof Element)) return;

        const slide = target.closest('.lun-hero__slide');
        if (!slide || !this.contains(slide)) return;

        this.#editorHold = true;
        const index = this.#slides.indexOf(/** @type {HTMLElement} */ (slide));
        if (index >= 0) this.#go(index, false);
        this.#updatePlaying();
    };

    #handleBlockDeselect = () => {
        this.#editorHold = false;
        this.#updatePlaying();
    };
}

if (!customElements.get('lun-hero-wrist-component')) {
    customElements.define('lun-hero-wrist-component', LunHeroWristComponent);
}
